/**
 * Notification Hooks
 * React Query hooks for notification management
 *
 * OPTIMISTIC UI STRATEGY:
 * - First open: Shows loading spinner (no cached data)
 * - Subsequent opens: Shows cached data immediately while refetching in background
 * - Background updates: Subtle pulsing dot indicator when refreshing
 * - Cost-effective: Only fetches when dropdown is open
 * - Smart caching: Data cached for 10 minutes, considered fresh for 2 minutes
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '../services/notification.service';
import { toast } from 'react-hot-toast';

// Type for API error responses
interface ApiError {
  response?: { data?: { message?: string } };
}

/**
 * Hook to fetch notifications for current user
 *
 * Optimistic UI + Cost-Effective Strategy:
 * - enabled: false by default (only fetches when dropdown opens)
 * - staleTime: 2 minutes (data fresh for 2 min, then refetch in background)
 * - gcTime: 10 minutes (keep cached data for 10 min for instant display)
 * - refetchOnMount: true (refresh when opening dropdown if data is stale)
 * - Shows cached data immediately while fetching fresh data in background
 */
export const useNotifications = (
  params?: { page?: number; limit?: number; unreadOnly?: boolean },
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: () => notificationService.getNotifications(params),
    staleTime: 0, // Always refetch when dropdown opens - ensures fresh data
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache for quick display while refetching
    refetchOnWindowFocus: true, // Refresh when user returns to tab
    refetchOnMount: 'always', // Always refetch when opening dropdown
    enabled: options?.enabled ?? false, // Disabled by default - fetch on-demand only
  });
};

/**
 * Hook to get unread notification count
 *
 * This can be enabled by default since it's a lightweight query
 * that just returns a number (minimal cost)
 *
 * Phase 2 Features:
 * - Smart polling: Optional polling with refetchInterval (e.g., 30000 for 30 seconds)
 * - Polling only works when tab is active (handled by React Query's default behavior)
 * - Set refetchInterval to enable smart polling (e.g., { refetchInterval: 30000 })
 */
export const useUnreadCount = (options?: {
  enabled?: boolean;
  refetchInterval?: number | false;
}) => {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationService.getUnreadCount(),
    staleTime: 30 * 1000, // 30 seconds - more responsive to new notifications
    gcTime: 5 * 60 * 1000, // 5 minutes (renamed from cacheTime in v5)
    refetchOnWindowFocus: true,
    refetchOnMount: 'always', // Always refetch - it's lightweight
    enabled: options?.enabled ?? true,
    // Smart polling: Only poll when tab is active (React Query default behavior)
    refetchInterval: options?.refetchInterval ?? false,
    refetchIntervalInBackground: false, // NEVER poll in background - cost-effective!
  });
};

/**
 * Hook to mark a notification as read
 */
export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationService.markAsRead(notificationId),
    onSuccess: () => {
      // Invalidate and refetch notifications
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.message || 'Failed to mark as read');
    },
  });
};

/**
 * Hook to mark all notifications as read
 */
export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: (data) => {
      // Invalidate and refetch all notification queries
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success(`Marked ${data.count} notifications as read`);
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.message || 'Failed to mark all as read');
    },
  });
};

/**
 * Hook to delete a notification
 */
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationService.deleteNotification(notificationId),
    onSuccess: () => {
      // Invalidate and refetch notifications
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('Notification deleted');
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.message || 'Failed to delete notification');
    },
  });
};
